type TodoAssignee = 'manager' | 'liver'

type OverviewCall = {
  id: string
  called_at: string
  todos: Array<{ task: string; assignee: TodoAssignee; completed?: boolean }> | null
  next_follow_date: string | null
}

type NextFollowState = 'today' | 'upcoming' | 'overdue'

type OverviewAssignment = {
  manager?: {
    name: string | null
    email: string
  } | null
} | null

type OverviewLineImport = {
  message_count: number
}

export type LiverOverview = {
  managerName: string
  callCount: number
  openTodoCount: number
  nextFollowDate: string | null
  nextFollowState: NextFollowState | null
  lineImportCount: number | null
  lineMessageCount: number | null
}

export function buildLiverOverview({
  currentAssignment,
  calls,
  lineImports,
  now = new Date()
}: {
  currentAssignment: OverviewAssignment
  calls: OverviewCall[]
  lineImports: OverviewLineImport[] | undefined
  now?: Date
}): LiverOverview {
  const nextFollow = getNextFollow(calls, now)

  return {
    managerName:
      currentAssignment?.manager?.name?.trim() ||
      currentAssignment?.manager?.email ||
      '未設定',
    callCount: calls.length,
    openTodoCount: countOpenTodos(calls),
    nextFollowDate: nextFollow?.date ?? null,
    nextFollowState: nextFollow?.state ?? null,
    lineImportCount: lineImports ? lineImports.length : null,
    lineMessageCount: lineImports
      ? lineImports.reduce((sum, lineImport) => sum + lineImport.message_count, 0)
      : null
  }
}

function countOpenTodos(calls: OverviewCall[]): number {
  return calls.reduce(
    (sum, call) =>
      sum +
      (call.todos ?? []).filter(
        (todo) => todo.task.trim().length > 0 && todo.completed !== true
      ).length,
    0
  )
}

function getNextFollow(
  calls: OverviewCall[],
  now: Date
): { date: string; state: NextFollowState } | null {
  const today = startOfLocalDay(now).getTime()
  const candidates = calls
    .filter((call) => call.next_follow_date)
    .map((call) => ({
      date: call.next_follow_date as string,
      time: parseLocalDate(call.next_follow_date as string).getTime()
    }))
    .filter((candidate) => Number.isFinite(candidate.time))
    .sort((a, b) => a.time - b.time)

  const upcoming = candidates.find((candidate) => candidate.time >= today)
  if (upcoming) {
    return {
      date: upcoming.date,
      state: upcoming.time === today ? 'today' : 'upcoming'
    }
  }

  const overdue = [...candidates].reverse().find((candidate) => candidate.time < today)
  if (!overdue) return null
  return {
    date: overdue.date,
    state: 'overdue'
  }
}

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}
